import React, { useState, useEffect, useRef } from 'react';
import { unknownErrorMessageDialog, yesNoMessageDialog } from '../ModalDialogsTemplates';
import { formModes, actionTypes } from '../../constants';
import i18next from "../../i18n";
import { getCurrentCompanyInfo, formatFloatTextValue, deepClone } from "../helpers/HelperFunctions";
import { FloatField } from './FloatField';
import { DateTimeField } from './DateTimeField';
import moment from 'moment';
import { ActionsPanel } from '../forms/ActionsPanel';
import { dispatcherActionTypes, globalEventDispatcher } from '../helpers/GlobalEventDispatcher';
import { v4 } from 'uuid';

const MutualSettlementDataField = (props) => {
    const [fieldId] = useState(v4());
    const [cofcoCompanyName, setCofcoCompanyName] = useState('');
    const [mutualSettlementData, setMutualSettlementData] = useState(null);
    const [changed, setChanged] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [allChecked, setAllChecked] = useState(false);
    const [checkedRows, setCheckedRows] = useState([]);
    const [cacheLoaded, setCacheLoaded] = useState(false);
    const [turnover, setTurnover] = useState({
        turnoverDebit: 0.0,
        turnoverCredit: 0.0,
        turnoverDebitCounterparty: 0.0,
        turnoverCreditCounterparty: 0.0,
        endSaldoCredit: 0.0,
        endSaldoDebit: 0.0,
        endSaldoCounterpartyCredit: 0.0,
        endSaldoCounterpartyDebit: 0.0
    });
    const [companyName] = useState(JSON.parse(localStorage?.getItem('user'))?.companies[0]?.companyShortName);
    const mutualSettlementDataRef = useRef(mutualSettlementData);

    useEffect(() => {
        getCurrentCompanyInfo().then(info => {
            if (info) {
                setCofcoCompanyName(info.name);
            }
        })
        const globalEventListerner = (actionType, params) => {
            if (actionType === dispatcherActionTypes.onUnselectTableAllRows) {
                onUncheckAll();
            }
            if (actionType === dispatcherActionTypes.onRowSelected) {
                if (params && params.tableName !== fieldId)
                    onUncheckAll();
            }
            if (actionType === dispatcherActionTypes.onShowMutualSettlementDataError) {
                onShowError();
            }
        }
        globalEventDispatcher.subscribe(globalEventListerner);
        return () => globalEventDispatcher.unsubscribe(globalEventListerner);
    }, [])

    useEffect(() => {
        if (props.docId) {
            fetch('Document/GetMutualSettlementData?docId=' + props.docId).then(response => {
                if (response.ok) {
                    response.json().then(result => {
                        setMutualSettlementData(result);
                        setLoaded(true);
                    });
                }
                else {
                    console.error('GetMutualSettlementData error with status: ' + response.status);
                    unknownErrorMessageDialog();
                }
            })
                .catch(err => {
                    console.error('GetMutualSettlementData error');
                    console.error(err);
                    unknownErrorMessageDialog();
                });
        }
    }, [props.docId]);

    useEffect(() => {
        loadFromCashe();
    }, [props.cashedData])

    useEffect(() => {
        mutualSettlementDataRef.current = mutualSettlementData;
        if (mutualSettlementData?.data) {
            let turnoverDebit = 0.0;
            let turnoverCredit = 0.0;
            let turnoverDebitCounterparty = 0.0;
            let turnoverCreditCounterparty = 0.0;
            mutualSettlementData.data.forEach(item => {
                turnoverDebit += item.debit ? item.debit : 0.0;
                turnoverCredit += item.credit ? item.credit : 0.0;
                if (item.counterpartyItem) {
                    turnoverDebitCounterparty += item.counterpartyItem.debit ? item.counterpartyItem.debit : 0.0;
                    turnoverCreditCounterparty += item.counterpartyItem.credit ? item.counterpartyItem.credit : 0.0;
                }
            });
            let endSaldoCredit = '';
            let endSaldoDebit = '';
            if (turnoverCredit + mutualSettlementData.creditSaldo > turnoverDebit + mutualSettlementData.debitSaldo) {
                endSaldoCredit = turnoverCredit - turnoverDebit + mutualSettlementData.creditSaldo - mutualSettlementData.debitSaldo;
            }
            else {
                endSaldoDebit = turnoverDebit - turnoverCredit + mutualSettlementData.debitSaldo - mutualSettlementData.creditSaldo;
            }
            let endSaldoCreditCounterparty = '';
            let endSaldoDebitCounterparty = '';
            if (turnoverDebitCounterparty + mutualSettlementData.counterpartyDebitSaldo > turnoverCreditCounterparty + mutualSettlementData.counterpartyCreditSaldo) {
                endSaldoDebitCounterparty = turnoverDebitCounterparty - turnoverCreditCounterparty + mutualSettlementData.counterpartyDebitSaldo - mutualSettlementData.counterpartyCreditSaldo;
            }
            else {
                endSaldoCreditCounterparty = turnoverCreditCounterparty - turnoverDebitCounterparty + mutualSettlementData.counterpartyCreditSaldo - mutualSettlementData.counterpartyDebitSaldo;
            }
            setTurnover({
                turnoverDebit: turnoverDebit,
                turnoverCredit: turnoverCredit,
                turnoverDebitCounterparty: turnoverDebitCounterparty,
                turnoverCreditCounterparty: turnoverCreditCounterparty,
                endSaldoCredit: endSaldoCredit,
                endSaldoDebit: endSaldoDebit,
                endSaldoCreditCounterparty: endSaldoCreditCounterparty,
                endSaldoDebitCounterparty: endSaldoDebitCounterparty
            });
        }
        if (props.onChange) {
            props.onChange(mutualSettlementData);
        }
        loadFromCashe();
    }, [mutualSettlementData])

    const loadFromCashe = () => {
        try {
            if (props.cashedData && mutualSettlementData && !cacheLoaded) {
                setCacheLoaded(true);
                const updatedData = deepClone(mutualSettlementData);
                updatedData.counterpartyDebitSaldo = props.cashedData.counterpartyDebitSaldo;
                updatedData.counterpartyCreditSaldo = props.cashedData.counterpartyCreditSaldo;

                if (props.cashedData.data && props.cashedData.data.length > 0) {
                    props.cashedData.data.forEach(item => {
                        const finded = updatedData.data?.find(it => it.row === item.row);
                        if (finded) {
                            finded.counterpartyItem = item.counterpartyItem;
                        }
                        else if (item.added) {
                            updatedData.data.push({
                                added: true,
                                row: item.row,
                                counterpartyItem: item.counterpartyItem
                            })
                        }
                    });
                }
                setMutualSettlementData(updatedData);
            }
        }
        catch (err) {
            console.error('loadFromCashe error:');
            console.error(err);
        }
    }

    const onSaldoChange = (e) => {
        const value = e.target.value;
        let updatedValue = parseFloat(value ? value.replaceAll(' ', '').replace(',', '.') : '0');
        if (Math.abs(updatedValue) < 0.01) updatedValue = 0;
        const updatedData = deepClone(mutualSettlementData);
        if (e.target.name === 'debitSaldo') {
            updatedData.counterpartyDebitSaldo = updatedValue;
        }
        else {
            updatedData.counterpartyCreditSaldo = updatedValue;
        }
        setChanged(true);
        setMutualSettlementData(updatedData);
    }

    const onInputChange = (e) => {
        const { name, value } = e.target;
        const nameParts = name.split('_');
        const updatedData = deepClone(mutualSettlementData);
        const row = updatedData.data.find(r => r.row === parseInt(nameParts[1]));
        if (row) {
            if (!row.counterpartyItem) row.counterpartyItem = { row: row.row };
            switch (nameParts[0]) {
                case 'debit': {
                    let updatedValue = parseFloat(value ? value.replaceAll(' ', '').replace(',', '.') : '0');
                    if (Math.abs(updatedValue) < 0.01) updatedValue = 0;
                    row.counterpartyItem.debit = updatedValue;
                    setChanged(true);
                    break;
                }
                case 'credit': {
                    let updatedValue = parseFloat(value ? value.replaceAll(' ', '').replace(',', '.') : '0');
                    if (Math.abs(updatedValue) < 0.01) updatedValue = 0;
                    row.counterpartyItem.credit = updatedValue;
                    setChanged(true);
                    break;
                }
                case 'date': {
                    row.counterpartyItem.date = value ? moment(value, "YYYY-MM-DD").format("DD.MM.YYYY") : null;
                    setChanged(true);
                    break;
                }
                case 'document': {
                    row.counterpartyItem.document = value;
                    setChanged(true);
                    break;
                }
            }
            setMutualSettlementData(updatedData);
        }
    }

    const onFillData = (rows = null) => {
        const fillData = () => {
            const updatedData = deepClone(mutualSettlementData);
            updatedData.data.filter(item => !item.added && (!rows || rows.includes(item.row))).forEach(item => {
                if (!item.counterpartyItem) item.counterpartyItem = { row: item.row };
                item.counterpartyItem.date = item.date;
                item.counterpartyItem.document = item.document;
                item.counterpartyItem.debit = item.credit;
                item.counterpartyItem.credit = item.debit;
            });
            updatedData.counterpartyDebitSaldo = updatedData.creditSaldo;
            updatedData.counterpartyCreditSaldo = updatedData.debitSaldo;
            setMutualSettlementData(updatedData);
            setChanged(false);
        }

        if (changed) {
            yesNoMessageDialog(i18next.t('attentionTitle'), i18next.t('mutualSettlementData.replaceAttentionMessage'), fillData);
        }
        else {
            fillData();
        }
    }

    const onAddRow = () => {
        const updatedData = deepClone(mutualSettlementData);
        let lastRow = updatedData.data[updatedData.data.length - 1].row + 1;
        updatedData.data.push({ row: lastRow, counterpartyItem: { row: lastRow }, added: true });
        setMutualSettlementData(updatedData);
        setAllChecked(false);
    }

    const onCheckAll = (e) => {
        if (e.target.checked) {
            if (mutualSettlementData.data) {
                setCheckedRows(mutualSettlementData.data.map(item => item.row));
                globalEventDispatcher.dispatch(dispatcherActionTypes.onRowSelected, { tableName: fieldId });
            }
        }
        else {
            setCheckedRows([]);
        }
    }

    const onUncheckAll = () => {
        setCheckedRows([]);
    }

    useEffect(() => {
        if (checkedRows.length === mutualSettlementData?.data?.length) {
            setAllChecked(true);
        }
        else {
            setAllChecked(false);
        }
    }, [checkedRows]);

    const onCheckRow = (e, row) => {
        if (e.target.checked) {
            setCheckedRows(prevRows => prevRows.concat(row));
            globalEventDispatcher.dispatch(dispatcherActionTypes.onRowSelected, { tableName: fieldId });
        }
        else {
            setCheckedRows(prevRows => prevRows.filter(prevRow => prevRow !== row));
        }
    }

    const getActions = () => {
        const result = [{
            btnClass: "btn-primary",
            isMultiaction: true,
            title: i18next.t("mutualSettlementData.fillDataBtn"),
            type: actionTypes.fill
        }];
        if (!checkedRows.some(row => !mutualSettlementData?.data?.find(item => item.row === row).added)) {
            result.push({
                btnClass: "btn-secondary",
                isMultiaction: true,
                title: i18next.t("mutualSettlementData.deleteRow"),
                type: actionTypes.remove
            });
        }
        if (mutualSettlementData?.data && checkedRows.length === 1) {
            if (checkedRows[0] !== mutualSettlementData.data[0].row) {
                result.push({
                    btnClass: "upRow btn-no-fill",
                    isMultiaction: false,
                    title: i18next.t("mutualSettlementData.upRow"),
                    type: actionTypes.up
                });
            }
            if (checkedRows[0] !== mutualSettlementData.data[mutualSettlementData.data.length - 1].row) {
                result.push({
                    btnClass: "downRow btn-no-fill",
                    isMultiaction: false,
                    title: i18next.t("mutualSettlementData.downRow"),
                    type: actionTypes.down
                });
            }
        }
        return result;
    }

    const onRowClick = (row) => {
        if (checkedRows.includes(row)) {
            setCheckedRows(prevRows => prevRows.filter(prevRow => prevRow !== row));
            globalEventDispatcher.dispatch(dispatcherActionTypes.onRowSelected, { tableName: fieldId });
        }
        else {
            setCheckedRows(prevRows => prevRows.concat(row));
        }
    }

    const onRemoveData = () => {
        const onRemove = () => {
            const updatedData = deepClone(mutualSettlementData);
            updatedData.data = updatedData.data.filter(item => !item.added || !checkedRows.includes(item.row));

            let row = 1;
            const notAddedRows = updatedData.data.filter(item => !item.added);
            if (notAddedRows.length > 0) {
                row = notAddedRows[notAddedRows.length - 1].row + 1;
            }
            const addedRows = updatedData.data.filter(item => item.added);
            for (let i = 0; i < addedRows.length; i++) {
                addedRows[i].row = row + i;
                addedRows[i].counterpartyItem.row = row + i;
            }

            setMutualSettlementData(updatedData);
            onUncheckAll();
        }
        yesNoMessageDialog(i18next.t('attentionTitle'), i18next.t('mutualSettlementData.removeMessage'), onRemove);
    }

    const onRowMove = (up) => {
        if (mutualSettlementData?.data && checkedRows.length === 1) {
            const updatedData = deepClone(mutualSettlementData);

            const current = updatedData.data.find(item => item.row === checkedRows[0]);
            const nextIndex = updatedData.data.indexOf(current) + (up ? -1 : 1);
            const next = updatedData.data[nextIndex];

            const tmp = current.counterpartyItem;

            current.counterpartyItem = next.counterpartyItem;
            if (current.counterpartyItem) current.counterpartyItem.row = current.row;

            next.counterpartyItem = tmp;
            if (next.counterpartyItem) next.counterpartyItem.row = next.row;

            setMutualSettlementData(updatedData);
            setCheckedRows([next.row]);
        }
    }

    const onAction = (type) => {
        switch (type) {
            case actionTypes.fill:
                onFillData(checkedRows);
                break;
            case actionTypes.remove:
                onRemoveData();
                break;
            case actionTypes.up:
                onRowMove(true);
                break;
            case actionTypes.down:
                onRowMove(false);
                break;
        }
    }

    const onShowError = () => {
        const data = mutualSettlementDataRef.current;
        const scrollToRow = (id) => {
            const element = document.getElementById(id ?? fieldId);
            element.scrollIntoView({ behavior: "smooth" });
            element.classList.add('error');
            setTimeout(() => element.classList.remove('error') , 2000);
        }

        if (!data) {
            scrollToRow();
            return;
        }

        const notEmptyItems = data.data.filter(item => item.counterpartyItem?.document || item.counterpartyItem?.date || item.counterpartyItem?.debit || item.counterpartyItem?.credit);
        if (notEmptyItems.length === 0) {
            scrollToRow();
            return;
        }
        if (data.counterpartyDebitSaldo && data.counterpartyCreditSaldo) {
            scrollToRow(fieldId + '_saldoRow');
            return;
        }
        let errorItem = notEmptyItems.find(item => !item.counterpartyItem?.document || !item.counterpartyItem.date || (!item.counterpartyItem.debit && !item.counterpartyItem.credit));
        if (errorItem) {
            scrollToRow(fieldId + '_row_' + errorItem.row);
            return false;
        }
        errorItem = notEmptyItems.find(item => item.counterpartyItem.debit && item.counterpartyItem.credit);
        if (errorItem) {
            scrollToRow(fieldId + '_row_' + errorItem.row);
            return;
        }
    }

    return <div className="mutual-settlement-data-input" id={fieldId} >
        <div className="table-block" key="table-block">
            <div className="table-response">
                <table>
                    <thead>
                        <tr key="firstTr" className="firstTr">
                            {props.formMode !== formModes.read && <th></th>}
                            <th colSpan="4" key="companyNameTh">{cofcoCompanyName && <span>{i18next.t("mutualSettlementData.tableHeader").replace("{companyName}", cofcoCompanyName)}</span>}</th>
                            <th colSpan="4" key="counterpartyCompanyNameTh">{companyName && [<span key="0">{i18next.t("mutualSettlementData.tableHeader").replace("{companyName}", companyName)}</span>, props.formMode !== formModes.read && <button key="1" className="btn btn-secondary" onClick={() => onFillData()} >{i18next.t("mutualSettlementData.fillDataBtn")}</button>]}</th>
                        </tr>
                        <tr key="secondTr">
                            {props.formMode !== formModes.read && <th key="0">
                                <input id={"mainCheckbox"} type="checkbox" onChange={onCheckAll} checked={!!allChecked} />
                                <label htmlFor={"mainCheckbox"}></label>
                            </th>}
                            <th key="dateHeader" className="dateHeader">
                                <span>{i18next.t("mutualSettlementData.dateHeader")}</span>
                            </th>
                            <th key="documentHeader" className="documentHeader">
                                <span>{i18next.t("mutualSettlementData.documentHeader")}</span>
                            </th>
                            <th key="debitHeader" className="debitHeader">
                                <span>{i18next.t("mutualSettlementData.debitHeader")}</span>
                            </th>
                            <th key="creditHeader" className="creditHeader">
                                <span>{i18next.t("mutualSettlementData.creditHeader")}</span>
                            </th>
                            <th key="dateHeader-counterparty" className="dateHeader">
                                <span>{i18next.t("mutualSettlementData.dateHeader")}</span>
                            </th>
                            <th key="documentHeader-counterparty" className="documentHeader">
                                <span>{i18next.t("mutualSettlementData.documentHeader")}</span>
                            </th>
                            <th key="debitHeader-counterparty" className="debitHeader">
                                <span>{i18next.t("mutualSettlementData.debitHeader")}</span>
                            </th>
                            <th key="creditHeader-counterparty" className="creditHeader">
                                <span>{i18next.t("mutualSettlementData.creditHeader")}</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr id={fieldId + '_saldoRow'} >
                            {props.formMode !== formModes.read && <td key="0"></td>}
                            <td colSpan="2" key="1"><b>{i18next.t("mutualSettlementData.startSaldoTitle")}</b></td>
                            <td className={"saldoCol" + (mutualSettlementData?.debitSaldo < 0.01 ? ' negative' : '')} key="2">{(!mutualSettlementData || Math.abs(mutualSettlementData.debitSaldo) < 0.01) ? '0,00' : formatFloatTextValue(mutualSettlementData.debitSaldo, 2)}</td>
                            <td className={"saldoCol" + (mutualSettlementData?.creditSaldo < 0.01 ? ' negative' : '')} key="3">{(!mutualSettlementData || Math.abs(mutualSettlementData.creditSaldo) < 0.01) ? '0,00' : formatFloatTextValue(mutualSettlementData.creditSaldo, 2)}</td>
                            <td colSpan="2" key="4"><b>{i18next.t("mutualSettlementData.startSaldoTitle")}</b></td>
                            {props.formMode !== formModes.read && <td key="5">{mutualSettlementData && <FloatField onChange={onSaldoChange} name="debitSaldo" className="form-control table-input" value={mutualSettlementData.counterpartyDebitSaldo ? mutualSettlementData.counterpartyDebitSaldo.toString().replace('.', ',') : '0,00'} accuracy={2} canUpdate={true} />}</td>}
                            {props.formMode === formModes.read && <td key="6" className={"saldoCol" + (mutualSettlementData?.counterpartyDebitSaldo < 0.01 ? ' negative' : '0,00')}>{(!mutualSettlementData || Math.abs(mutualSettlementData.counterpartyDebitSaldo) < 0.01) ? '0,00' : formatFloatTextValue(mutualSettlementData.counterpartyDebitSaldo, 2)}</td>}
                            {props.formMode !== formModes.read && <td key="7">{mutualSettlementData && <FloatField onChange={onSaldoChange} name="creditSaldo" className="form-control table-input" value={mutualSettlementData.counterpartyCreditSaldo ? mutualSettlementData.counterpartyCreditSaldo.toString().replace('.', ',') : '0,00'} accuracy={2} canUpdate={true} />}</td>}
                            {props.formMode === formModes.read && <td key="8" className={"saldoCol" + (mutualSettlementData?.counterpartyCreditSaldo < 0.01 ? ' negative' : '0,00')}>{(!mutualSettlementData || Math.abs(mutualSettlementData.counterpartyCreditSaldo) < 0.01) ? '0,00' : formatFloatTextValue(mutualSettlementData.counterpartyCreditSaldo, 2)}</td>}
                        </tr>
                        {mutualSettlementData && mutualSettlementData.data && mutualSettlementData.data.map((item, index) => <tr key={"row_" + index} id={fieldId + '_row_' + item.row}>
                            {props.formMode !== formModes.read && <td key="0">
                                <input id={"rowCheckbox_" + item.row} type="checkbox" onChange={(e) => onCheckRow(e, item.row)} checked={checkedRows.includes(item.row)} />
                                <label htmlFor={"rowCheckbox_" + item.row}></label>
                            </td>}
                            <td key={"date_" + index} onClick={() => onRowClick(item.row)}>{item.date}</td>
                            <td key={"doc_" + index} onClick={() => onRowClick(item.row)}>{item.document}</td>
                            <td key={"debit_" + index} onClick={() => onRowClick(item.row)} className={"debitCol" + (item.debit < 0.01 ? ' negative' : '')}>{Math.abs(item.debit) < 0.01 ? '' : formatFloatTextValue(item.debit, 2)}</td>
                            <td key={"credit_" + index} onClick={() => onRowClick(item.row)} className={"creditCol" + (item.credit < 0.01 ? ' negative' : '')} >{Math.abs(item.credit) < 0.01 ? '' : formatFloatTextValue(item.credit, 2)}</td>
                            {props.formMode === formModes.read ? <>
                                <td key={"c_date_" + index}>{item.counterpartyItem?.date}</td>
                                <td key={"c_doc_" + index}>{item.counterpartyItem?.document}</td>
                                <td key={"c_debit_" + index} className={"debitCol" + (item.counterpartyItem?.debit < 0.01 ? ' negative' : '')}>{Math.abs(item.counterpartyItem?.debit) < 0.01 ? '' : formatFloatTextValue(item.counterpartyItem?.debit, 2)}</td>
                                <td key={"c_credit_" + index} className={"creditCol" + (item.counterpartyItem?.credit < 0.01 ? ' negative' : '')}>{Math.abs(item.counterpartyItem?.credit) < 0.01 ? '' : formatFloatTextValue(item.counterpartyItem?.credit, 2)}</td>
                            </> : <>
                                    <td key={"c_date_" + index}><DateTimeField onChange={onInputChange} name={"date_" + item.row} value={item.counterpartyItem?.date ? moment(item.counterpartyItem.date, "DD.MM.YYYY").format("YYYY-MM-DD") : null} format="date" canUpdate={true} /></td>
                                    <td key={"c_doc_" + index}><input onChange={onInputChange} className="form-control" name={"document_" + item.row} value={item.counterpartyItem?.document ?? ''} /></td>
                                    <td key={"c_debit_" + index} className="debitCol"><FloatField onChange={onInputChange} name={"debit_" + item.row} className="form-control table-input" value={item.counterpartyItem?.debit ? item.counterpartyItem.debit.toString().replace('.', ',') : ''} accuracy={2} showEmpty={true} canUpdate={true} /></td>
                                    <td key={"c_credit_" + index} className="creditCol"><FloatField onChange={onInputChange} name={"credit_" + item.row} className="form-control table-input" value={item.counterpartyItem?.credit ? item.counterpartyItem.credit.toString().replace('.', ',') : ''} accuracy={2} showEmpty={true} canUpdate={true} /></td>
                            </>}
                        </tr>)}
                        <tr>
                            {props.formMode !== formModes.read && <td key="0"></td>}
                            <td key="1" colSpan="2"><b>{i18next.t("mutualSettlementData.turnoverTitle")}</b></td>
                            <td key="2" className={"turnoverCol" + (turnover.turnoverDebit < 0.01 ? ' negative' : '')}>{Math.abs(turnover.turnoverDebit) < 0.01 ? '' : formatFloatTextValue(turnover.turnoverDebit, 2)}</td>
                            <td key="3" className={"turnoverCol" + (turnover.turnoverCredit < 0.01 ? ' negative' : '')}>{Math.abs(turnover.turnoverCredit) < 0.01 ? '' : formatFloatTextValue(turnover.turnoverCredit, 2)}</td>
                            <td key="4" colSpan="2"><b>{i18next.t("mutualSettlementData.turnoverTitle")}</b></td>
                            <td key="5" className={"turnoverCol" + (turnover.turnoverDebitCounterparty < 0.01 ? ' negative' : '')}>{Math.abs(turnover.turnoverDebitCounterparty) < 0.01 ? '' : formatFloatTextValue(turnover.turnoverDebitCounterparty, 2)}</td>
                            <td key="6" className={"turnoverCol" + (turnover.turnoverCreditCounterparty < 0.01 ? ' negative' : '')}>{Math.abs(turnover.turnoverCreditCounterparty) < 0.01 ? '' : formatFloatTextValue(turnover.turnoverCreditCounterparty, 2)}</td>
                        </tr>
                        <tr>
                            {props.formMode !== formModes.read && <td key="0"></td>}
                            <td key="1" colSpan="2"><b>{i18next.t("mutualSettlementData.endSaldoTitle")}</b></td>
                            <td key="2" className={"saldoCol" + (turnover.endSaldoDebit < 0.01 ? ' negative' : '')}>{formatFloatTextValue(turnover.endSaldoDebit, 2)}</td>
                            <td key="3" className={"saldoCol" + (turnover.endSaldoCredit < 0.01 ? ' negative' : '')}>{formatFloatTextValue(turnover.endSaldoCredit, 2)}</td>
                            <td key="4" colSpan="2"><b>{i18next.t("mutualSettlementData.endSaldoTitle")}</b></td>
                            <td key="5" className={"saldoCol" + (turnover.endSaldoDebitCounterparty < 0.01 ? ' negative' : '')}>{formatFloatTextValue(turnover.endSaldoDebitCounterparty, 2)}</td>
                            <td key="6" className={"saldoCol" + (turnover.endSaldoCreditCounterparty < 0.01 ? ' negative' : '')}>{formatFloatTextValue(turnover.endSaldoCreditCounterparty, 2)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        {props.formMode !== formModes.read && loaded && <button className="btn btn-secondary" onClick={onAddRow} key="addRowBtn" >{i18next.t("mutualSettlementData.addRow")}</button>}
        <div className="form-block">
            <div className="form-content">
                <div className="row comment-row">
                    <div className="form-group col-sm-12 col-xs-12">
                        <label htmlFor="prop_sxcfc_remark" className="">{i18next.t("mutualSettlementData.commentLabel")}</label>
                        <textarea name="prop_sxcfc_remark" className="form-control" rows="4" disabled={props.formMode === formModes.read} value={props.comment} onChange={props.onCommentChanged}  />
                    </div>
                </div>
            </div>
        </div>
        {props.formMode !== formModes.read && checkedRows.length > 0 &&
            <ActionsPanel actions={getActions()} handlerUncheckAll={onUncheckAll} actionHandlerCallback={onAction} checkedCount={checkedRows.length} />
        }
    </div>
}

export default MutualSettlementDataField;